import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Megaphone, TrendingUp, Sparkles, Globe } from "lucide-react";
import { HERO_SLIDES, LOGO_URL } from "@/lib/constants";
import { useLang } from "@/contexts/LangContext";

const ICONS = { launch: Megaphone, invest: TrendingUp, ai: Sparkles };

export default function HeroCarousel() {
  const { lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [emblaRef, embla] = useEmblaCarousel(
    { loop: true, direction: isRTL ? "rtl" : "ltr", align: "start", startIndex: 0, containScroll: false },
    [Autoplay({ delay: 6000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [idx, setIdx] = useState(0);

  const onSelect = useCallback(() => { if (embla) setIdx(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect); embla.on("reInit", onSelect); onSelect();
  }, [embla, onSelect]);

  // Percentages tuned so text never overlaps the logo panel.
  // On mobile there is no side logo panel — image is a background with a full-width overlay.
  return (
    <section className="relative w-full bg-[#062C22]" data-testid="hero-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {HERO_SLIDES.map((s, i) => {
            const Icon = ICONS[s.key] || Sparkles;
            return (
              <div key={s.key} className="min-w-0 flex-[0_0_100%]">
                <Link to={s.href} data-testid={`hero-slide-${s.key}`}
                      aria-label={lang === "ar" ? s.title_ar : s.title_en}
                      className="block group">
                  <div className="relative w-full aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9] lg:aspect-[2.55/1] max-h-[640px] overflow-hidden">
                    {/* Background image */}
                    <img src={s.img} alt=""
                         className="absolute inset-0 w-full h-full object-cover transition-transform duration-[6000ms] group-hover:scale-[1.04]"
                         loading={i === 0 ? "eager" : "lazy"}
                         fetchpriority={i === 0 ? "high" : "auto"} />

                    {/* Deep-green start (logo side) fade */}
                    <div className="absolute inset-y-0 start-0 w-[42%] sm:w-[30%] md:w-[26%] bg-gradient-to-e from-[#062C22] via-[#062C22]/85 to-transparent" style={{ backgroundImage: `linear-gradient(to ${isRTL ? "left" : "right"}, #062C22, rgba(6,44,34,.85), transparent)` }} />

                    {/* Emerald end fade */}
                    <div className="absolute inset-y-0 end-0 w-[14%] md:w-[10%]" style={{ backgroundImage: `linear-gradient(to ${isRTL ? "right" : "left"}, #0D5C46, rgba(13,92,70,.7), transparent)` }} />

                    {/* Mobile-only readability veil */}
                    <div className="sm:hidden absolute inset-0 bg-[#062C22]/40" />

                    {/* LEFT/START LOGO PANEL (desktop) */}
                    <div className="hidden sm:flex absolute inset-y-0 start-0 w-[26%] md:w-[22%] lg:w-[20%] flex-col items-center justify-center px-4 md:px-6 gap-3 z-10 pointer-events-none">
                      <div className="w-20 md:w-28 lg:w-32 aspect-square rounded-full bg-white/95 shadow-[0_20px_40px_-15px_rgba(0,0,0,.5)] grid place-items-center overflow-hidden">
                        <img src={LOGO_URL} alt="FLUORIT" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-center">
                        <div className="text-white font-extrabold text-lg md:text-xl tracking-wide">FLUORIT</div>
                        <div className="text-[#EBDDA6] text-[10px] md:text-xs font-semibold mt-0.5">
                          {lang === "ar" ? "فلوريت للاستثمارات العقارية" : "REAL ESTATE INVESTMENTS"}
                        </div>
                        <div className="text-white/70 text-[10px] md:text-[11px] mt-2 leading-relaxed max-w-[180px] mx-auto">
                          {lang === "ar" ? "نستثمر في الأرض .. ونبني قيمة للمستقبل" : "We invest in land — building value for the future"}
                        </div>
                      </div>
                    </div>

                    {/* TEXT BLOCK — safe area guaranteed by ps-/pe- */}
                    <div className="relative z-10 h-full w-full flex flex-col justify-center px-6 sm:px-0 sm:ps-[30%] md:ps-[26%] lg:ps-[24%] sm:pe-[8%] md:pe-[10%] lg:pe-[12%]">
                      <div className="max-w-2xl text-center sm:text-start mx-auto sm:mx-0">
                        <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-bold text-[#062C22] bg-[#EBDDA6] px-3 py-1.5 rounded-full mb-3 sm:mb-4 shadow-[0_6px_20px_-8px_rgba(183,150,46,.9)]">
                          <Icon className="w-3.5 h-3.5" />
                          {lang === "ar" ? s.tag_ar : s.tag_en}
                        </div>
                        <h1 className="text-white font-extrabold tracking-tight text-[22px] leading-[1.15] sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl drop-shadow-[0_6px_20px_rgba(0,0,0,.55)]">
                          {lang === "ar" ? s.title_ar : s.title_en}
                        </h1>
                        <p className="mt-2 sm:mt-3 md:mt-4 text-white/90 text-xs sm:text-sm md:text-base lg:text-lg max-w-xl drop-shadow mx-auto sm:mx-0">
                          {lang === "ar" ? s.sub_ar : s.sub_en}
                        </p>
                        <div className="mt-4 sm:mt-5 md:mt-6 flex flex-wrap items-center gap-3 justify-center sm:justify-start">
                          <span className="inline-flex items-center gap-2 bg-[#B7962E] hover:bg-[#a3831f] text-[#062C22] font-bold h-10 sm:h-11 md:h-12 px-5 md:px-7 rounded-full shadow-[0_14px_36px_-10px_rgba(183,150,46,.8)] transition text-sm md:text-base">
                            {lang === "ar" ? s.cta_ar : s.cta_en}
                            <Arrow className="w-4 h-4" />
                          </span>
                          <span className="hidden md:inline-flex items-center gap-1.5 text-white/85 text-xs font-semibold">
                            <Globe className="w-3.5 h-3.5" /> fluoritestate.com
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* END VERTICAL DECORATIVE STRIP (desktop) */}
                    <div className="hidden md:flex absolute inset-y-0 end-0 w-[6%] items-center justify-center z-10 pointer-events-none">
                      <div className="w-1 h-1/3 bg-gradient-to-b from-[#B7962E] via-[#EBDDA6] to-transparent rounded-full" />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots */}
      <div className="absolute z-20 inset-x-0 bottom-3 sm:bottom-5 flex items-center justify-center gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} aria-label={`slide ${i + 1}`} data-testid={`hero-dot-${i}`}
                  onClick={(e) => { e.preventDefault(); embla && embla.scrollTo(i); }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === i ? "w-8 bg-[#B7962E]" : "w-2.5 bg-white/60 hover:bg-white"
                  }`} />
        ))}
      </div>
    </section>
  );
}
